import { Link } from "react-router-dom";
import { useState } from "react";
import { useCart } from "../context/CartContext";
import { Trash2, ShoppingBag, Minus, Plus, MessageSquareHeart, X } from "lucide-react";

const NOTE_MAX = 200;

function NoteEditor({ item, onSave, onCancel }) {
  const [val, setVal] = useState(item.note || "");
  return (
    <div className="mt-2" data-testid={`cart-note-editor-${item.product_id}`}>
      <textarea
        value={val}
        onChange={(e) => setVal(e.target.value.slice(0, NOTE_MAX))}
        rows={2}
        placeholder="Type your personalised message…"
        className="w-full px-3 py-2 border-2 border-[#2D2424] rounded-2xl bg-[#FFFAF3] font-hand text-sm focus:outline-none focus:border-[#7A1F2B] resize-none"
        style={{ boxShadow: "2px 2px 0 #2D2424" }}
      />
      <div className="mt-1 flex items-center justify-between">
        <span className="text-[11px] font-hand text-[#5C5555]">{val.length}/{NOTE_MAX}</span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            data-testid={`cart-note-cancel-${item.product_id}`}
            className="text-xs px-3 py-1 border-2 border-[#2D2424] rounded-full font-hand bg-white hover:bg-[#FFE7F1]"
          >Cancel</button>
          <button
            type="button"
            onClick={() => onSave(val.trim())}
            data-testid={`cart-note-save-${item.product_id}`}
            className="text-xs px-3 py-1 rounded-full font-hand bg-[#7A1F2B] text-white hover:bg-[#5C1520]"
          >Save</button>
        </div>
      </div>
    </div>
  );
}

export default function Cart() {
  const { items, updateQty, removeItem, updateNote, total } = useCart();
  const [editing, setEditing] = useState(null);

  return (
    <div className="pt-32 pb-20" data-testid="cart-page">
      <div className="container-x">
        <p className="label-small">Your Selection</p>
        <h1 className="font-serif text-5xl text-[#2D2424] mt-3">Shopping Cart</h1>

        {items.length === 0 ? (
          <div className="text-center py-20" data-testid="cart-empty">
            <ShoppingBag className="w-12 h-12 mx-auto text-[#7A1F2B]" strokeWidth={1}/>
            <p className="font-serif text-2xl mt-4 text-[#2D2424]">Your cart is empty</p>
            <Link to="/shop/her" className="btn-primary mt-6 inline-flex">Continue Shopping</Link>
          </div>
        ) : (
          <div className="mt-12 grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-4">
              {items.map((it) => {
                const rowKey = `${it.product_id}::${it.variant_id || ""}`;
                return (
                <div key={rowKey} className="bg-white border border-[#E5DBD8] p-4" data-testid={`cart-item-${it.product_id}`}>
                  <div className="flex gap-4">
                    <div className="w-24 h-24 bg-[#F2E8E4] shrink-0 overflow-hidden">
                      <img src={it.image} alt={it.name} className="w-full h-full object-cover"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <Link to={`/product/${it.slug}`} className="font-serif text-lg text-[#2D2424] hover:text-[#7A1F2B]">{it.name}</Link>
                      {it.variant_color_name && (
                        <p className="text-xs font-hand text-[#5C5555] mt-0.5 flex items-center gap-1.5" data-testid={`cart-variant-${rowKey}`}>
                          <span className="inline-block w-3 h-3 rounded-full border border-[#2D2424]" style={{ backgroundColor: it.variant_color_hex || "#FFE7F1" }}/>
                          Colour: <span className="text-[#2D2424]">{it.variant_color_name}</span>
                        </p>
                      )}
                      <p className="text-sm text-[#7A1F2B] mt-1">₹{it.price.toLocaleString("en-IN")}</p>
                      <div className="flex items-center gap-3 mt-3">
                        <div className="flex items-center border border-[#E5DBD8]">
                          <button onClick={() => updateQty(it.product_id, it.quantity - 1, it.variant_id)} data-testid={`cart-minus-${it.product_id}`} className="px-2 py-1 hover:bg-[#F2E8E4]"><Minus className="w-3 h-3"/></button>
                          <span className="px-3 text-sm">{it.quantity}</span>
                          <button onClick={() => updateQty(it.product_id, it.quantity + 1, it.variant_id)} data-testid={`cart-plus-${it.product_id}`} className="px-2 py-1 hover:bg-[#F2E8E4]"><Plus className="w-3 h-3"/></button>
                        </div>
                        <button onClick={() => removeItem(it.product_id, it.variant_id)} data-testid={`cart-remove-${it.product_id}`} className="text-[#7A1F2B] hover:text-[#5C1520]"><Trash2 className="w-4 h-4" strokeWidth={1.5}/></button>
                      </div>
                    </div>
                    <p className="font-serif text-lg text-[#2D2424] self-start">₹{(it.price * it.quantity).toLocaleString("en-IN")}</p>
                  </div>

                  {/* Personalised note row */}
                  <div className="mt-3 pt-3 border-t border-dashed border-[#E5DBD8]" data-testid={`cart-note-row-${it.product_id}`}>
                    {editing === rowKey ? (
                      <NoteEditor
                        item={it}
                        onSave={(val) => { updateNote(it.product_id, val, it.variant_id); setEditing(null); }}
                        onCancel={() => setEditing(null)}
                      />
                    ) : it.note ? (
                      <div className="flex items-start gap-2 bg-[#FFFAF3] border border-dashed border-[#7A1F2B] p-2 rounded-md">
                        <MessageSquareHeart className="w-4 h-4 text-[#7A1F2B] mt-0.5 shrink-0"/>
                        <p className="flex-1 italic font-hand text-sm text-[#2D2424] whitespace-pre-line">&ldquo;{it.note}&rdquo;</p>
                        <button
                          onClick={() => setEditing(rowKey)}
                          data-testid={`cart-note-edit-${it.product_id}`}
                          className="text-xs font-hand text-[#7A1F2B] underline shrink-0"
                        >Edit</button>
                        <button
                          onClick={() => updateNote(it.product_id, "", it.variant_id)}
                          data-testid={`cart-note-remove-${it.product_id}`}
                          className="text-[#5C5555] hover:text-[#7A1F2B] shrink-0"
                          aria-label="remove note"
                        ><X className="w-3.5 h-3.5"/></button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditing(rowKey)}
                        data-testid={`cart-note-add-${it.product_id}`}
                        className="inline-flex items-center gap-1.5 text-sm font-hand text-[#7A1F2B] hover:text-[#5C1520]"
                      >
                        <MessageSquareHeart className="w-4 h-4"/> Add a personalised note
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>

            <div className="bg-[#F2E8E4]/50 border border-[#E5DBD8] p-6 h-fit" data-testid="cart-summary">
              <h3 className="font-serif text-2xl text-[#2D2424]">Order Summary</h3>
              {(() => {
                const FREE_AT = 2999;
                const shipping = total >= FREE_AT ? 0 : 89;
                const grand = total + shipping;
                const remainingForFree = Math.max(0, FREE_AT - total);
                return (
                  <>
                    {/* Free-shipping nudge */}
                    {total > 0 && shipping > 0 && (
                      <div
                        className="mt-4 p-3 bg-[#FFF7DC] border-2 border-dashed border-[#E08036] rounded-xl -rotate-1"
                        style={{ boxShadow: "3px 3px 0 #E08036" }}
                        data-testid="cart-free-shipping-nudge"
                      >
                        <p className="font-doodle text-sm text-[#7A1F2B] leading-tight">
                          ✨ Add ₹{remainingForFree.toLocaleString("en-IN")} more for <b>FREE shipping!</b>
                        </p>
                      </div>
                    )}
                    {total >= FREE_AT && (
                      <div
                        className="mt-4 p-3 bg-[#EAFBE7] border-2 border-dashed border-green-700 rounded-xl rotate-1"
                        style={{ boxShadow: "3px 3px 0 #2D6A4F" }}
                        data-testid="cart-free-shipping-unlocked"
                      >
                        <p className="font-doodle text-sm text-green-800 leading-tight">
                          🎉 You&apos;ve unlocked <b>FREE shipping!</b>
                        </p>
                      </div>
                    )}
                    <div className="mt-5 space-y-2 text-sm">
                      <div className="flex justify-between"><span>Subtotal</span><span data-testid="cart-subtotal">₹{total.toLocaleString("en-IN")}</span></div>
                      <div className="flex justify-between text-[#5C5555]">
                        <span>Shipping</span>
                        <span data-testid="cart-shipping">
                          {shipping === 0 ? <span className="text-green-700 font-semibold">FREE</span> : `₹${shipping}`}
                        </span>
                      </div>
                    </div>
                    <div className="border-t border-[#E5DBD8] my-5"></div>
                    <div className="flex justify-between font-serif text-xl">
                      <span>Total</span>
                      <span className="text-[#7A1F2B]" data-testid="cart-total">₹{grand.toLocaleString("en-IN")}</span>
                    </div>
                    <Link to="/checkout" data-testid="cart-checkout-btn" className="btn-primary w-full mt-6">Proceed to Checkout</Link>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
